import { useId, useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  CaretDownIcon,
  CaretRightIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import {
  TASK_OUTCOME_FIELD_TYPES,
  type TaskOutcomeField,
} from '@erp/shared-types';
import { Input } from 'src/components/ui/input';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import { Switch } from 'src/components/ui/switch';
import { Toggle } from 'src/components/ui/toggle';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from 'src/components/ui/select';
import { TaskOutcomeOptionsEditor } from './TaskOutcomeOptionsEditor';
import { fieldOutcomes } from './taskOutcomeResultModel';
import { useFieldTypeLabels } from './useFieldTypeLabel';

/**
 * One result field as seen from a single outcome: what it is called, its type,
 * whether this outcome requires it, and (expanded) its instructions, choices
 * and the other outcomes that record it too.
 */
export function TaskOutcomeFieldRow({
  field,
  outcome,
  vocabulary,
  autoFocus,
  readOnly = false,
  onLabelChange,
  onChange,
  onRequiredChange,
  onOutcomeToggle,
  onRemove,
}: {
  field: TaskOutcomeField;
  outcome: string;
  vocabulary: readonly string[];
  autoFocus: boolean;
  /**
   * Editing is locked: the surrounding fieldset disables every control,
   * the disclosure included, so details are shown open instead.
   */
  readOnly?: boolean;
  onLabelChange: (label: string) => void;
  onChange: (patch: Partial<TaskOutcomeField>) => void;
  onRequiredChange: (required: boolean) => void;
  onOutcomeToggle: (outcome: string, included: boolean) => void;
  onRemove: () => void;
}) {
  const id = useId();
  const needsOptions = field.type === 'select' && !field.options?.some(Boolean);
  // Opens for a new field or a choice field without choices, then stays put
  // so typing the first choice does not collapse the panel under the cursor.
  const [expanded, setExpanded] = useState(autoFocus || needsOptions);
  const showDetails = readOnly || expanded;
  const owners = fieldOutcomes(field, vocabulary);
  const others = owners.filter((entry) => entry !== outcome);

  return (
    <li className="flex flex-col gap-2 rounded-md border bg-background p-2.5">
      <div className="flex flex-wrap items-start gap-2">
        {!readOnly && (
          <DetailsToggle
            detailsId={`${id}-details`}
            expanded={expanded}
            onToggle={() => setExpanded(!expanded)}
          />
        )}
        <FieldNameAndKey
          id={id}
          field={field}
          others={others}
          autoFocus={autoFocus}
          onLabelChange={onLabelChange}
          onKeyChange={(key) => onChange({ key })}
        />
        <FieldRowControls
          field={field}
          outcome={outcome}
          shared={others.length > 0}
          onTypeChange={(patch) => {
            if (patch.type === 'select') setExpanded(true);
            onChange(patch);
          }}
          onRequiredChange={onRequiredChange}
          onRemove={onRemove}
        />
      </div>
      {showDetails && (
        <FieldDetails
          id={id}
          field={field}
          outcome={outcome}
          owners={owners}
          vocabulary={vocabulary}
          onChange={onChange}
          onOutcomeToggle={onOutcomeToggle}
        />
      )}
    </li>
  );
}

/** Type, required switch and remove button at the end of a field row. */
function FieldRowControls({
  field,
  outcome,
  shared,
  onTypeChange,
  onRequiredChange,
  onRemove,
}: {
  field: TaskOutcomeField;
  outcome: string;
  /** Other outcomes record this field too, so remove only drops this one. */
  shared: boolean;
  onTypeChange: (patch: Partial<TaskOutcomeField>) => void;
  onRequiredChange: (required: boolean) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  return (
    <div className="ml-auto flex items-center gap-2 pl-10 sm:pl-0">
      <FieldTypeSelect field={field} onChange={onTypeChange} />
      <Label className="flex h-8 items-center gap-2 text-sm font-normal">
        <Switch
          checked={field.requiredFor.includes(outcome)}
          onCheckedChange={onRequiredChange}
        />
        <T context="toggle: task outcome result field is required">Required</T>
      </Label>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label={
          shared
            ? gt('Remove from {outcome}', {
                context: 'remove a shared result field from one outcome',
                outcome,
              })
            : gt('Delete field', {
                context: 'delete a task outcome result field',
              })
        }
        onClick={onRemove}
      >
        <TrashIcon />
      </Button>
    </div>
  );
}

function DetailsToggle({
  detailsId,
  expanded,
  onToggle,
}: {
  detailsId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const gt = useGT();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-8"
      aria-expanded={expanded}
      aria-controls={detailsId}
      aria-label={gt('Field details', {
        context: 'expand task outcome result field details',
      })}
      onClick={onToggle}
    >
      {expanded ? <CaretDownIcon /> : <CaretRightIcon />}
    </Button>
  );
}

function FieldNameAndKey({
  id,
  field,
  others,
  autoFocus,
  onLabelChange,
  onKeyChange,
}: {
  id: string;
  field: TaskOutcomeField;
  others: string[];
  autoFocus: boolean;
  onLabelChange: (label: string) => void;
  onKeyChange: (key: string) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex min-w-48 flex-1 flex-col gap-1">
      <Input
        aria-label={gt('Field name', {
          context: 'task outcome result field display name',
        })}
        placeholder={gt('e.g. Approved amount', {
          context: 'placeholder for a task outcome result field name',
        })}
        className="h-8"
        autoFocus={autoFocus}
        value={field.label}
        onChange={(event) => onLabelChange(event.target.value)}
      />
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <label htmlFor={`${id}-key`} className="shrink-0">
          <T context="task outcome result field machine key prefix">Key</T>
        </label>
        <input
          id={`${id}-key`}
          className="min-w-0 flex-1 rounded-sm bg-transparent font-mono text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring"
          spellCheck={false}
          placeholder={gt('fieldKey', {
            context: 'placeholder for a task outcome result field key',
          })}
          value={field.key}
          onChange={(event) => onKeyChange(event.target.value)}
        />
        {others.length > 0 && (
          <span className="shrink-0 truncate rounded-full bg-muted px-1.5 py-0.5">
            <T context="badge: other outcomes that also record this result field">
              Also in <Var>{others.join(', ')}</Var>
            </T>
          </span>
        )}
      </div>
    </div>
  );
}

function FieldTypeSelect({
  field,
  onChange,
}: {
  field: TaskOutcomeField;
  onChange: (patch: Partial<TaskOutcomeField>) => void;
}) {
  const gt = useGT();
  const typeLabels = useFieldTypeLabels();
  return (
    <Select
      value={field.type}
      onValueChange={(value) => {
        const type = TASK_OUTCOME_FIELD_TYPES.find((entry) => entry === value);
        if (!type) return;
        onChange({
          type,
          options: type === 'select' ? (field.options ?? ['']) : undefined,
        });
      }}
    >
      <SelectTrigger
        className="h-8 w-32"
        aria-label={gt('Field type', {
          context: 'task outcome result field data type picker',
        })}
      >
        <SelectValue>{typeLabels[field.type]}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {TASK_OUTCOME_FIELD_TYPES.map((type) => (
            <SelectItem key={type} value={type}>
              {typeLabels[type]}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function FieldDetails({
  id,
  field,
  outcome,
  owners,
  vocabulary,
  onChange,
  onOutcomeToggle,
}: {
  id: string;
  field: TaskOutcomeField;
  outcome: string;
  owners: string[];
  vocabulary: readonly string[];
  onChange: (patch: Partial<TaskOutcomeField>) => void;
  onOutcomeToggle: (outcome: string, included: boolean) => void;
}) {
  const gt = useGT();
  const ownerSet = new Set(owners);
  return (
    <div
      id={`${id}-details`}
      className="flex flex-col gap-3 border-t pt-2.5 sm:ml-10"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-help`} className="text-xs">
          <T context="task outcome result field instructions">
            Instructions / evidence
          </T>
        </Label>
        <Input
          id={`${id}-help`}
          className="h-8"
          placeholder={gt('What should the closer record here?', {
            context:
              'placeholder for instructions on a task outcome result field',
          })}
          value={field.helpText ?? ''}
          onChange={(event) => onChange({ helpText: event.target.value })}
        />
      </div>
      <Label className="flex items-start gap-2 text-sm font-normal">
        <Switch
          className="mt-0.5"
          checked={field.showAsBadge ?? false}
          onCheckedChange={(checked) =>
            onChange({ showAsBadge: checked || undefined })
          }
        />
        <span className="flex flex-col gap-0.5">
          <T context="toggle: show a task outcome result value as a badge on task lists">
            Show as badge
          </T>
          <span className="text-xs text-muted-foreground">
            <T context="help text for showing a task outcome result value as a badge">
              Display the recorded value under the task where it is listed on
              its linked record, such as the invoice page.
            </T>
          </span>
        </span>
      </Label>
      {field.type === 'select' && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium">
            <T context="choices for a task outcome result choice field">
              Choices
            </T>
          </span>
          <TaskOutcomeOptionsEditor
            options={field.options}
            onChange={(options) => onChange({ options })}
          />
        </div>
      )}
      {vocabulary.length > 1 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium">
            <T context="which outcomes record this result field">
              Recorded with
            </T>
          </span>
          <div className="flex flex-wrap gap-1.5">
            {vocabulary.map((entry) => (
              <Toggle
                key={entry}
                size="sm"
                variant="outline"
                className="h-7 font-mono text-xs"
                pressed={ownerSet.has(entry)}
                disabled={entry === outcome}
                onPressedChange={(pressed) => onOutcomeToggle(entry, pressed)}
              >
                {ownerSet.has(entry) ? <CheckIcon /> : <PlusIcon />}
                {entry}
              </Toggle>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
